import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import { FiX, FiBriefcase, FiSquare, FiAward, FiArchive, FiLayers, FiShoppingCart, FiMessageSquare } from 'react-icons/fi';
import './SearchAdmin.css';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const RESOURCES = [
  { key: 'bureau', label: 'Bureau', icon: <FiBriefcase />, to: '/admin/Bureaux' },
  { key: 'table', label: 'Table', icon: <FiSquare />, to: '/admin/Table' },
  { key: 'chaise', label: 'Chaise', icon: <FiAward />, to: '/admin/Chaise' },
  { key: 'armoire', label: 'Armoire', icon: <FiArchive />, to: '/admin/Armoire' },
  { key: 'comptoir', label: 'Comptoir', icon: <FiLayers />, to: '/admin/Comptoir' },
];

const SearchAdmin = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const authorization = localStorage.getItem('token');

  const [dataset, setDataset] = useState({
    bureau: [], table: [], chaise: [], armoire: [], comptoir: [], commande: [], contact: [],
  });

  useEffect(() => {
    const headers = { Authorization: `Bearer ${authorization}` };
    RESOURCES.forEach((r) => {
      axios.get(`${API}/${r.key}/lister`).then((res) => {
        setDataset((prev) => ({ ...prev, [r.key]: res.data }));
      }).catch(() => {});
    });
    axios.get(`${API}/commande/lister`, { headers }).then((res) => {
      setDataset((prev) => ({ ...prev, commande: res.data }));
    }).catch(() => {});
    axios.get(`${API}/contact/lister`, { headers }).then((res) => {
      setDataset((prev) => ({ ...prev, contact: res.data }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];
    const match = (str) => typeof str === 'string' && str.toLowerCase().includes(term);
    const out = [];

    RESOURCES.forEach((r) => {
      dataset[r.key]
        .filter((p) => match(p.titre) || match(p.reference))
        .slice(0, 4)
        .forEach((p) => out.push({ key: `${r.key}-${p._id}`, icon: r.icon, label: p.titre, sub: r.label, to: r.to }));
    });

    dataset.commande
      .filter((c) => match(c.nom) || match(c.prenom) || match(c.numero) || match(c._id))
      .slice(0, 4)
      .forEach((c) => out.push({
        key: `commande-${c._id}`,
        icon: <FiShoppingCart />,
        label: `${c.prenom || ''} ${c.nom || ''}`.trim() || `Commande #${c._id.slice(-6)}`,
        sub: 'Commande',
        to: '/admin/Commande',
      }));

    dataset.contact
      .filter((m) => match(m.sujet) || match(m.email))
      .slice(0, 4)
      .forEach((m) => out.push({ key: `contact-${m._id}`, icon: <FiMessageSquare />, label: m.sujet || m.email, sub: 'Réclamation', to: '/admin/Reclamation' }));

    return out.slice(0, 10);
  }, [search, dataset]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToResult = (to) => {
    setIsFocused(false);
    setSearch('');
    navigate(to);
  };

  return (
    <div className="admin-search-container" ref={inputRef}>
      <Form className={`admin-search-form ${isFocused ? 'focused' : ''}`} onSubmit={(e) => e.preventDefault()}>
        <div className="search-icon-wrapper">
          <svg className="search-icon" viewBox="0 0 24 24">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </div>

        <Form.Control
          type="search"
          placeholder="Rechercher un produit, une commande…"
          className="admin-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsFocused(true)}
        />

        {search && (
          <button type="button" className="admin-search-clear" onClick={() => setSearch('')}>
            <FiX size={13} />
          </button>
        )}
      </Form>

      {isFocused && search.trim().length >= 2 && (
        <div className="admin-search-suggestions">
          {results.length === 0 ? (
            <div className="admin-search-empty">Aucun résultat pour « {search} »</div>
          ) : (
            results.map((r) => (
              <button key={r.key} type="button" className="admin-search-item" onClick={() => goToResult(r.to)}>
                <span className="admin-search-item-icon">{r.icon}</span>
                <span className="admin-search-item-content">
                  <span className="admin-search-item-label">{r.label || 'Sans titre'}</span>
                  <span className="admin-search-item-sub">{r.sub}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAdmin;
